import { Observable, timer } from 'rxjs';
import { last, share, switchMap, takeWhile, tap } from 'rxjs/operators';

export interface PollingOptions<T> {
  /**
   * ogni quanto viene eseguita l'azione (ms)
   */
  interval?: number;
  /**
   * numero massimo di tentavi
   */
  maxAttemps?: number;
  /**
   * tempo massimo (ms)
   */
  maxTime?: number;

  /**
   * l'osservabile emette ad ogni chiamata che viene fatta
   */
  continuous?: boolean;

  /**
   * funzione per determinare fin quando eseguire il polling
   */
  takeWhile?: (res: T) => boolean;
}

export class Polling<T> {
  private options: PollingOptions<T>;

  private attemps: number;

  private elapsedTime: number;

  private startedAt: number;

  constructor(options: PollingOptions<T>) {
    this.options = { ...options };
  }

  run(action: Observable<T>): Observable<T> {
    this.setStart();

    const operators = [];

    return timer(0, this.getIntervalTime()).pipe(
      switchMap(() => action),
      share(),
      tap(() => {
        this.addAttempt();
        this.addElapsedTime();
      }),
      takeWhile(res => this.isCompleted(res), true),
      this.options.continuous ? tap() : last()
    );
  }

  private isCompleted(res: T): boolean {
    if (this.attemps >= this.getMaxAttempts()) {
      return false;
    } else if (this.elapsedTime >= this.getMaxTime()) {
      return false;
    } else if (typeof this.options.takeWhile === 'function') {
      return this.options.takeWhile(res);
    } else {
      return true;
    }
  }

  private setStart(): void {
    this.startedAt = new Date().getTime();
    this.attemps = 0;
    this.elapsedTime = 0;
  }

  private addAttempt(): void {
    this.attemps++;
  }

  private addElapsedTime(): void {
    this.elapsedTime = new Date().getTime() - this.startedAt;
  }

  private getIntervalTime(): number {
    return typeof this.options.interval === 'number'
      ? this.options.interval
      : 5000;
  }

  private getMaxAttempts(): number {
    return typeof this.options.maxAttemps === 'number'
      ? this.options.maxAttemps
      : Infinity;
  }

  private getMaxTime(): number {
    return typeof this.options.maxTime === 'number'
      ? this.options.maxTime
      : Infinity;
  }
}
